import { HeaderTitleState } from '@app/storage/headerTitle'
import { AppBar, Chip, styled, Toolbar, Typography } from '@mui/material'
import { FC, useCallback, useState } from 'react'
import { useSnapshot } from 'valtio'
import TimeLimitsContainer from '@app/components/ui/containers/TimeLimitsContainer'
import { AssessmentTest } from '@app/models'
import {
  useCurrentAssessmentSetup,
  useDefineTimeLimit,
  useTestCallback,
  useUserAssessmentFetch,
} from '@app/helpers'
import { RETURN_TO_DASHBOARD_BTN_ATTRIBUTES } from '@app/constants'
import { BasicModal } from '@app/components/ui/modals/BasicModal'
import { CurrentTestState, CurrentUserAssessmentState } from '@app/storage'
import TestToolsMenu from './TestToolsMenu'
import { AssessmentStatus } from '@app/firebase/api'
import { focusStyle } from '@app/theme'
import { ScreenReaderInfo } from './ScreenReaderInfo'
import SkipLinks from './SkipLinks'

interface ScreenHeaderProps {
  icon?: JSX.Element
  test?: AssessmentTest
  previewMode?: boolean
}

const ScreenHeader: FC<ScreenHeaderProps> = ({ icon, test, previewMode = false }) => {
  const { title } = useSnapshot(HeaderTitleState)
  const { assessmentId, status } = useSnapshot(CurrentUserAssessmentState)
  const { isStarted } = useSnapshot(CurrentTestState)
  const maxTime = useDefineTimeLimit()

  const { userAssessment, loading: fetchLoading } = useUserAssessmentFetch()
  const packageLoading = useCurrentAssessmentSetup(!userAssessment)
  const loading = fetchLoading || packageLoading

  const onTestCallback = useTestCallback()

  const [showTimeoutModal, setShowTimeoutModal] = useState<boolean>(false)

  const onTimeOut = useCallback(() => {
    setShowTimeoutModal(true)
  }, [])

  const isCompleted = status === AssessmentStatus.completed

  return (
    <>
      <SkipLinks />

      {loading && (
        <ScreenReaderInfo ariaBusy ariaAtomic ariaLive='assertive'>
          Loading Test...
        </ScreenReaderInfo>
      )}

      <StyledAppBar position='static' tabIndex={-1}>
        <StyledToolbar>
          {icon || null}

          {previewMode && <PreviewChip label='Preview' size='small' />}

          <TestTitle component='h1' variant='h3' title={title}>
            {title}
          </TestTitle>

          {assessmentId && (
            <RightPartContainer>
              {isStarted && Boolean(test) && !test.isFinished && (
                <TimeLimitsContainer
                  maxTime={maxTime}
                  resourceId={test.resourceId}
                  allowLateSubmission={test.timeLimits?.allowLateSubmission}
                  onTimeOut={onTimeOut}
                />
              )}

              <TestToolsMenu isReviewMode={isCompleted} />
            </RightPartContainer>
          )}
        </StyledToolbar>

        <BasicModal
          open={showTimeoutModal}
          title='Time Expired'
          backdropDisabled
          onClose={() => setShowTimeoutModal(false)}
          onSubmit={() => onTestCallback()}
          noCancel
          submitText={'Leave Test'}
          // modalProps={SUBMIT_MODAL_ATTRIBUTES}
          submitButtonProps={RETURN_TO_DASHBOARD_BTN_ATTRIBUTES}
        >
          <Typography variant='variation_subtitle1'>
            Your test submitted automatically when time expired.
          </Typography>

          <Typography variant='variation_subtitle1'>Leave the test to see your score results.</Typography>
        </BasicModal>
      </StyledAppBar>
    </>
  )
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: theme.shadows[6],
  height: theme.spacing(16.5),

  '&:focus-visible': {
    ...focusStyle(theme).border,
  },

  [theme.breakpoints.down('mobile')]: {
    height: theme.spacing(14.5),
  },
}))

const StyledToolbar = styled(Toolbar)(
  ({ theme }) => `
    background: ${theme.palette.grey[800]};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100%;
    padding: ${theme.spacing(0, 5)};

    /* a11y: white focus ring for the controls on this dark toolbar (back, timer,
       accessibility, tools), overriding the global blue ring that fails 3:1 here (SC 1.4.11) */
    & button:focus,
    & button:focus-visible {
      outline: none;
      box-shadow: ${theme.mixins.focusRingOnDark.shadow.boxShadow} !important;
    }

    ${[theme.breakpoints.down('mobile')]} {
      padding: ${theme.spacing(0, 2)};
    }
  `,
)

const TestTitle = styled(Typography)(
  ({ theme }) => `
    font-size: 1.25rem;
    line-height: 1.375rem;
    margin-bottom: 0;
    color: ${theme.palette.common.white};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 ${theme.spacing(2)} 0 ${theme.spacing(6)};

    ${[theme.breakpoints.down('mobile')]} {
      margin-left: ${theme.spacing(2)};
    }
  `,
) as typeof Typography

const PreviewChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(86,126,33,0.3)',
  borderRadius: 100,
  color: theme.palette.lightGreen[200],
  fontSize: 14,
  fontWeight: 400,
  height: 26,
  lineHeight: '16px',
  marginLeft: theme.spacing(6),
  paddingLeft: 2,
  paddingRight: 2,

  '& .MuiChip-label': {
    paddingLeft: 6,
    paddingRight: 6,
  },

  [theme.breakpoints.down('mobile')]: {
    marginLeft: theme.spacing(2),
  },
}))

const RightPartContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(5),
  marginLeft: 'auto',

  [theme.breakpoints.down('mobile')]: {
    gap: theme.spacing(3),
  },
}))

export default ScreenHeader
